import { useEffect, useMemo, useState } from "react";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import DataTable from "../../components/DataTable";
import SalesInvoiceForm from "../../components/generalTrade/SalesInvoiceForm";
import { gtApi } from "../../api/generalTradeApi";
import { useFormat } from "../../../context/FormatContext";
import { useBusy } from "../../../context/BusyContext";
import { useMessageModal } from "../../contexts/MessageModalContext";

type ScreenMode = "list" | "add" | "edit" | "view";

export default function SalesInvoices() {
  const { formatDateToDMY, formatDateOnly, formatCurrency } = useFormat();

  const { showModal } = useMessageModal();
  const { runBusy } = useBusy();

  const [rows, setRows] = useState<any[]>([]);

  const [availableDos, setAvailableDos] = useState<any[]>([]);

  const [selected, setSelected] = useState<any>(null);

  const [selectedDo, setSelectedDo] = useState<any>(null);

  const [screenMode, setScreenMode] = useState<ScreenMode>("list");

  const [activeInvoiceId, setActiveInvoiceId] = useState<number | null>(null);

  const [importOpen, setImportOpen] = useState(false);

  const [search, setSearch] = useState("");

  const load = async (): Promise<void> => {
    try {
      const [invoices, deliveryOrders] = await runBusy(() =>
        Promise.all([
          gtApi.salesInvoices(),
          gtApi.availableDeliveryOrdersForInvoice(),
        ]),
      );

      setRows(Array.isArray(invoices) ? invoices : []);

      setAvailableDos(Array.isArray(deliveryOrders) ? deliveryOrders : []);
    } catch (error) {
      console.error("Failed to load Sales Invoices:", error);

      setRows([]);
      setAvailableDos([]);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const filteredDos = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) {
      return availableDos;
    }

    return availableDos.filter((row) =>
      [
        row.delivery_order_number,
        row.sales_order_number,
        row.company_name,
        row.delivery_date,
        row.grand_total,
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(keyword),
      ),
    );
  }, [availableDos, search]);

  const columns = [
    {
      key: "sales_invoice_number",
      label: "SI No.",
    },
    {
      key: "delivery_order_number",
      label: "DO No.",
    },
    {
      key: "sales_order_number",
      label: "SO No.",
    },
    {
      key: "invoice_date",
      label: "Invoice Date",
      render: (row: any) =>
        row.invoice_date ? formatDateOnly(row.invoice_date) : "-",
    },
    {
      key: "company_name",
      label: "Customer",
    },
    {
      key: "subtotal",
      label: "Subtotal",
      render: (row: any) => formatCurrency(Number(row.subtotal || 0)),
    },
    {
      key: "discount_amount",
      label: "Discount",
      render: (row: any) => formatCurrency(Number(row.discount_amount || 0)),
    },
    {
      key: "grand_total",
      label: "Grand Total",
      render: (row: any) => formatCurrency(Number(row.grand_total || 0)),
    },
    {
      key: "paid_amount",
      label: "Paid",
      render: (row: any) => formatCurrency(Number(row.paid_amount || 0)),
    },
    {
      key: "outstanding_amount",
      label: "Outstanding",
      render: (row: any) => formatCurrency(Number(row.outstanding_amount || 0)),
    },
    {
      key: "payment_status",
      label: "Payment Status",
      render: (row: any) => {
        const status = String(row.payment_status || "");

        return (
          <span
            className={
              status === "paid"
                ? "badge-success"
                : status === "partially_paid"
                  ? "badge-warning"
                  : "badge-danger"
            }
          >
            {status
              .replace(/_/g, " ")
              .replace(/\b\w/g, (character) => character.toUpperCase())}
          </span>
        );
      },
    },
    {
      key: "created_at",
      label: "Created At",
      render: (row: any) =>
        row.created_at ? formatDateToDMY(row.created_at) : "-",
    },
    {
      key: "updated_at",
      label: "Updated At",
      render: (row: any) =>
        row.updated_at ? formatDateToDMY(row.updated_at) : "-",
    },
    {
      key: "status",
      label: "Status",
      render: (row: any) => (
        <span
          className={
            row.status === "submitted"
              ? "badge-success"
              : row.status === "cancelled"
                ? "badge-danger"
                : "badge-warning"
          }
        >
          {String(row.status || "").replace(/\b\w/g, (c) => c.toUpperCase())}
        </span>
      ),
    },
  ];

  const doColumns = [
    {
      key: "delivery_order_number",
      label: "DO No.",
    },
    {
      key: "sales_order_number",
      label: "SO No.",
    },
    {
      key: "delivery_date",
      label: "Date",
      render: (row: any) => formatDateOnly(row.delivery_date),
    },
    {
      key: "company_name",
      label: "Customer",
    },
    {
      key: "grand_total",
      label: "Amount",
      render: (row: any) => formatCurrency(Number(row.grand_total || 0)),
    },
    {
      key: "status",
      label: "Status",
      render: () => <span className="badge-success">Submitted</span>,
    },
  ];

  const changeSelectedInvoiceStatus = async (): Promise<void> => {
    if (!selected) {
      showModal({
        type: "error",
        title: "Sales Invoice Required",
        message: "Please select a Sales Invoice first.",
        confirmText: "OK",
      });

      return;
    }

    const isCancelled =
      String(selected.status || "").toLowerCase() === "cancelled";

    /*
     * =========================
     * REACTIVATE
     * =========================
     */
    if (isCancelled) {
      showModal({
        type: "warning",
        title: "Reactivate Sales Invoice?",
        message:
          `Sales Invoice ${selected.sales_invoice_number} will be restored to Submitted status. ` +
          "Reactivation is only possible if its Delivery Order has not been used for another active Sales Invoice.",

        confirmText: "Yes, Activate",

        onConfirm: async () => {
          try {
            const response = await runBusy(() =>
              gtApi.reactivateSalesInvoice(Number(selected.id)),
            );

            showModal({
              type: "success",
              title: "Sales Invoice Reactivated",
              message:
                response?.message ||
                `${selected.sales_invoice_number} has been reactivated successfully.`,
              confirmText: "OK",
            });

            setSelected(null);

            await load();
          } catch (error: any) {
            showModal({
              type: "error",
              title: "Unable to Reactivate Sales Invoice",
              message:
                error?.response?.data?.message ||
                "Unable to reactivate the Sales Invoice.",
              confirmText: "OK",
            });
          }
        },
      });

      return;
    }

    /*
     * =========================
     * CANCEL
     * =========================
     */
    if (Number(selected.paid_amount || 0) > 0) {
      showModal({
        type: "error",
        title: "Sales Invoice Has Payment",
        message:
          `${selected.sales_invoice_number} has ${formatCurrency(
            Number(selected.paid_amount || 0),
          )} applied against it. ` +
          "Cancel or reverse the related Customer Payment first.",
        confirmText: "OK",
      });

      return;
    }

    showModal({
      type: "warning",
      title: "Cancel Sales Invoice?",
      message:
        `This will cancel ${selected.sales_invoice_number}. ` +
        "Its Delivery Order will become available for invoicing again. Continue?",

      confirmText: "Yes, Cancel",

      onConfirm: async () => {
        try {
          const response = await runBusy(() =>
            gtApi.deleteSalesInvoice(Number(selected.id)),
          );

          showModal({
            type: "success",
            title: "Sales Invoice Cancelled",
            message:
              response?.message ||
              `${selected.sales_invoice_number} has been cancelled successfully.`,
            confirmText: "OK",
          });

          setSelected(null);

          await load();
        } catch (error: any) {
          showModal({
            type: "error",
            title: "Unable to Cancel Sales Invoice",
            message:
              error?.response?.data?.message ||
              "Unable to cancel the Sales Invoice.",
            confirmText: "OK",
          });
        }
      },
    });
  };

  const closeForm = () => {
    setScreenMode("list");
    setActiveInvoiceId(null);
    setSelectedDo(null);
  };

  const handleSaved = () => {
    closeForm();
    setSelected(null);
    void load();
  };

  if (screenMode !== "list") {
    return (
      <SalesInvoiceForm
        mode={screenMode}
        invoiceId={activeInvoiceId}
        deliveryOrder={selectedDo}
        onCancel={closeForm}
        onSaved={handleSaved}
      />
    );
  }

  return (
    <>
      <AdminListToolkit
        title="SALES INVOICES"
        columns={columns}
        data={rows}
        selectedRow={selected}
        onAdd={() => {
          setSelectedDo(null);
          setSearch("");
          setImportOpen(true);
        }}
        onView={(row) => {
          setActiveInvoiceId(Number(row.id));
          setScreenMode("view");
        }}
        onEdit={(row) => {
          setActiveInvoiceId(Number(row.id));
          setScreenMode("edit");
        }}
        onSoftDelete={() => {
          void changeSelectedInvoiceStatus();
        }}
        softDeleteLabel={selected?.status === "cancelled" ? "Active" : "Delete"}
        softDeleteIsActivate={selected?.status === "cancelled"}
        editDisabled={selected?.status === "cancelled"}
        enableExport
        exportFileName="sales-invoices"
        extraButtons={[
          {
            label: "PDF",
            onClick: () => {
              if (!selected) return;

              window.open(
                gtApi.pdfUrl("sales-invoices", selected.id),
                "_blank",
              );
            },
          },
          {
            label: "Share",
            onClick: async () => {
              if (!selected) return;

              const url = gtApi.pdfUrl("sales-invoices", selected.id);

              if (navigator.share) {
                await navigator.share({
                  title: selected.sales_invoice_number,
                  url,
                });
              } else {
                await navigator.clipboard.writeText(url);

                showModal({
                  type: "success",
                  title: "Link Copied",
                  message: "Sales Invoice PDF link copied.",
                  confirmText: "OK",
                });
              }
            },
          },
        ]}
      >
        {(filtered) => (
          <DataTable
            columns={columns}
            data={filtered}
            selectedRowId={selected?.id}
            onRowSelect={setSelected}
          />
        )}
      </AdminListToolkit>

      {importOpen && (
        <div className="gt-import-modal-overlay">
          <div className="gt-import-modal">
            <div className="gt-import-modal-header">
              <div>
                <h2>IMPORT DELIVERY ORDER</h2>

                <p>
                  Select a submitted Delivery Order that has not already been
                  invoiced.
                </p>
              </div>

              <button
                type="button"
                className="gt-import-modal-close"
                onClick={() => setImportOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="gt-import-modal-search">
              <input
                type="search"
                placeholder="Search DO, SO, customer, date or amount..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />

              <span>
                {filteredDos.length} record
                {filteredDos.length === 1 ? "" : "s"} found
              </span>
            </div>

            <div className="gt-import-modal-table">
              {filteredDos.length ? (
                <DataTable
                  columns={doColumns}
                  data={filteredDos}
                  selectedRowId={selectedDo?.id}
                  onRowSelect={setSelectedDo}
                />
              ) : (
                <div className="gt-import-modal-empty">
                  No Delivery Orders are available for invoicing.
                </div>
              )}
            </div>

            <div className="gt-import-selection">
              {selectedDo ? (
                <div className="gt-import-selection-title">
                  Selected: <strong>{selectedDo.delivery_order_number}</strong>{" "}
                  — {selectedDo.company_name}
                </div>
              ) : (
                <div className="gt-import-selection-title">
                  Select a Delivery Order from the listing.
                </div>
              )}
            </div>

            <div className="gt-import-modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setImportOpen(false)}
              >
                Cancel
              </button>

              <button
                type="button"
                className="btn-primary"
                disabled={!selectedDo}
                onClick={() => {
                  setImportOpen(false);
                  setScreenMode("add");
                }}
              >
                Create Sales Invoice
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
